import { Input, PageContainer, PageHeader } from "@touche-lts/panel-ui/dist";
import { IconLegal } from "assets/icons";
import { LegalStatuses } from "mobx-stores/legal/legal-table/legal-table-store.types";
import { useRootStore } from "mobx-stores/store-provider";
import { LegalTableEntityDTO } from "models/legal-table-entry";
import { useCallback, useEffect, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useParams } from "react-router";
import styles from "./legal-editor.module.scss";
import QuillToolbar from "./quill-toolbar";

// Undo and redo functions for Custom Toolbar
function undoChange() {
  this.quill.history.undo();
}
function redoChange() {
  this.quill.history.redo();
}

// Add sizes to whitelist and register them
const Size = Quill.import("formats/size");
Size.whitelist = ["extra-small", "small", "medium", "large"];
Quill.register(Size, true);

// Add fonts to whitelist and register them
const Font = Quill.import("formats/font");
Font.whitelist = [
  "arial",
  "comic-sans",
  "courier-new",
  "georgia",
  "helvetica",
  "lucida",
];
Quill.register(Font, true);

// Modules object for setting up the Quill editor
// const [modules] = useState({
//   toolbar: {
//     container: "#toolbar",
//     handlers: {
//       undo: undoChange,
//       redo: redoChange
//     }
//   },
//   history: {
//     delay: 500,
//     maxStack: 100,
//     userOnly: true
//   }
// });

const modules = {
  toolbar: {
    container: "#toolbar",
    handlers: {
      undo: undoChange,
      redo: redoChange,
    },
  },
  history: {
    delay: 500,
    maxStack: 100,
    userOnly: true,
  },
};

// Formats objects for setting up the Quill editor
const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "align",
  "strike",
  "script",
  "blockquote",
  "background",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "color",
  "code-block",
];

const MIN_LENGTH = 50;

const stripHtml = (html: string) =>
  html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

const LegalEditorPageComponent = () => {
  const {
      routingStore: { push },
      generalStore: {
        api: {
          legalApi: { getLegalPage, addLegalPage, updateLegalPage },
        },
      },
    } = useRootStore(),
    { legalId } = useParams<{ legalId: string }>(),
    [viewReady, setVeiwReady] = useState(false),
    [state, setState] = useState({
      content: "",
      status: LegalStatuses.ACTIVE,
      title: undefined,
      createdDate: undefined,
    }),
    handleChangeTitle = (title: string): void => {
      setState({ ...state, title });
    },
    handleChangeContent = useCallback(
      (content: string): void => {
        setState({ ...state, content });
      },
      [state],
    ),
    validate = (target: { content?: string }) =>
      stripHtml(target.content || "").length >= MIN_LENGTH,
    handleSaveEditContent = async (
      publishUnpublishPayload?: LegalTableEntityDTO,
    ): Promise<LegalTableEntityDTO> => {
      const target = publishUnpublishPayload ?? state;
      if (!validate(target)) return;
      if (legalId) {
        const legalPage = await updateLegalPage(target);
        if (!legalPage) return;
        push("/legal");
      } else {
        const legalPage = await addLegalPage(state);
        if (!legalPage) return;
        push("/legal");
      }
    },
    handlePublishUnpublish = (): void => {
      let mainSatate = null;
      if (state?.status === LegalStatuses.ACTIVE) {
        mainSatate = { ...state, status: LegalStatuses.ARCHIVED };
      } else {
        mainSatate = { ...state, status: LegalStatuses.ACTIVE };
      }
      handleSaveEditContent(mainSatate);
    };

  // declare the async data fetching function
  const fetchLegalPages = useCallback(async () => {
    const data = await getLegalPage(legalId);
    if (!data) return;
    setState({ ...state, ...(data as any) });
    setVeiwReady(true);
  }, []);

  useEffect(() => {
    if (legalId) {
      fetchLegalPages().catch(console.error);
      return;
    }
    setVeiwReady(true);
    return () => {
      setState({
        content: "",
        status: LegalStatuses.ACTIVE,
        title: null,
        createdDate: null,
      });
    };
  }, [fetchLegalPages]);

  return (
    <PageContainer>
      <PageHeader
        navSegments={[
          {
            icon: IconLegal,
            label: "Legal Pages",
            iconProps: { viewBox: "0 0 22 22" },
          },
        ]}
        buttons={[
          {
            preset: "secondary",
            children: "Discard",
            onClick: () => push("/legal"),
          },
          {
            preset: "primary",
            children: "Save",
            onClick: () => handleSaveEditContent(),
            disabled: !state?.title?.trim() || stripHtml(state?.content || "").length < MIN_LENGTH,
          },
          {
            preset: "primary",
            children:
              state?.status === LegalStatuses.ACTIVE ? "Unpublish" : "Publish",
            onClick: () => handlePublishUnpublish(),
            hidden: !legalId,
          },
        ]}
      />
      {viewReady && (
        <div className={styles.contentBox}>
          <Input
            placeholder="Page headline"
            value={state?.title}
            onChange={handleChangeTitle}
            style={{ marginBottom: 20 }}
          />
          <QuillToolbar />
          {/** IF u want more simplified version then uncomment the following and comment the other */}
          {/* <ReactQuill theme="snow" value={value} onChange={setValue} formats={[
            'header', 'font', 'size',
            'bold', 'italic', 'underline', 'strike', 'blockquote',
            'list', 'bullet', 'indent',
            'link', 'image', 'color',
          ]}/> */}
          <ReactQuill
            theme="snow"
            value={state?.content}
            onChange={handleChangeContent}
            placeholder={"Write something..."}
            modules={modules}
            formats={formats}
          />
          <div className={styles.charCountRow}>
            <span
              className={`${styles.charCount} ${stripHtml(state?.content || "").length < MIN_LENGTH ? styles.charCountError : ""}`}
            >
              {stripHtml(state?.content || "").length} characters
              {stripHtml(state?.content || "").length < MIN_LENGTH &&
                ` (${MIN_LENGTH - stripHtml(state?.content || "").length} more needed)`}
            </span>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

const LegalEditorPage = LegalEditorPageComponent;
export { LegalEditorPage };
